import React, { useEffect, useState } from 'react'
import GiftReceivePageView from 'src/pages/GiftCardPage/GiftReceivePage/view'
import { GiftCardPageProps } from 'src/pages/GiftCardPage'
import useGiftReceiveForm from 'src/hooks/react-hook-form/useGiftReceiveForm.hook'
import { FormObjectType, GiftReceiveFormItemsType } from 'src/types/react-hook-form'
import {
  GiftDetailInfoByGiftIdxSpace,
  GiftReceiveAcceptSpace,
  GiftReceiveRejectSpace,
} from 'src/types/api'
import { FormProvider } from 'react-hook-form'
import { HyphenReplace } from 'src/utils/regExps_util'
import Modal from 'src/components/common/Modal'
import RenderIf from 'src/components/common/RenderIf'
import { useModal } from 'src/provider/ModalContextProvider'
import {
  giftReceiveAcceptQueryOption,
  giftReceiveRejectQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { Loading } from 'src/components/common/Loading'
import { DateUtils } from 'src/utils/date'
import { ERRCODE_SUCCESS, ERRCODES_GIFT_VALID_CHECK } from 'src/service/constant/errcodes'

export interface GiftReceivePageProps {
  registerAttribute: Partial<Record<keyof GiftReceiveFormItemsType, Partial<FormObjectType>>>
  giftDetailData: GiftDetailInfoByGiftIdxSpace.Response
  inputLock: boolean
  handleInputCheck: () => void
  handleGiftAccept: () => void
  handleGiftReject: () => void
}

const GiftReceivePage = ({ giftDetailData }: GiftCardPageProps) => {
  const { isOpen: modalOpen, openModal } = useModal()
  const [inputLock, setInputLock] = useState(false)
  const { getAfter7Days, now } = DateUtils

  const { registerAttribute, methods } = useGiftReceiveForm()
  const { trigger, setValue, getValues } = methods

  const { SendType, RecipientPNumber, GiftIdx, RegDT } = giftDetailData
  const isKakaoSend = SendType === 0

  //선물 수락하기
  const { mutateAsync: giftReceiveAcceptMutate, isPending: isGiftReceiveAcceptPending } =
    useCustomMutation<GiftReceiveAcceptSpace.Request, GiftReceiveAcceptSpace.Response>(
      giftReceiveAcceptQueryOption(),
    )

  //선물 거절하기
  const { mutateAsync: giftReceiveRejectMutate, isPending: isGiftReceiveRejectPending } =
    useCustomMutation<GiftReceiveRejectSpace.Request, GiftReceiveRejectSpace.Response>(
      giftReceiveRejectQueryOption(),
    )

  const giftAcceptSubmit = () => {
    giftReceiveAcceptMutate({
      GiftIdx: GiftIdx,
      RecipientPNumber: getValues('recipientPNumber').replaceAll('-', ''),
      TermsAgree: 1,
    }).then(({ Header }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        window.location.reload()
      } else if (Header.ErrCode === ERRCODES_GIFT_VALID_CHECK.ERROR_RECEIVE_AFTER_7DAYS) {
        openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
      }
    })
  }

  const giftRejectSubmit = () => {
    giftReceiveRejectMutate({
      GiftIdx: GiftIdx,
    }).then(() => {
      window.location.reload()
    })
  }

  const handleGiftAccept = () => {
    if (now() > getAfter7Days(RegDT).endOf('day')) {
      openModal({
        contents: {
          d: '선물 받기 기간이 지났습니다.',
        },
        button: { submit: { t: '확인' } },
      })
    } else {
      giftAcceptSubmit()
    }
  }

  const handleGiftReject = () => {
    openModal({
      contents: {
        t: '선물을 거절하시겠습니까?',
        d: '거절은 취소 불가능하며, 선물을 보낸 분께 알림이 발송됩니다.',
      },
      button: { submit: { t: '확인', f: giftRejectSubmit }, cancel: { t: '취소' } },
    })
  }

  const handleInputCheck = async () => {
    if (inputLock) setInputLock(false)
    else {
      await trigger('recipientPNumber').then((isValid) => {
        setInputLock(isValid)
      })
    }
  }

  useEffect(() => {
    if (!isKakaoSend) {
      setValue('recipientPNumber', HyphenReplace(RecipientPNumber))
      setInputLock(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKakaoSend])

  const props: GiftReceivePageProps = {
    registerAttribute,
    giftDetailData,
    inputLock,
    handleInputCheck,
    handleGiftAccept,
    handleGiftReject,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={modalOpen}>
        <Modal />
      </RenderIf>
      <RenderIf when={!giftDetailData || isGiftReceiveAcceptPending || isGiftReceiveRejectPending}>
        <Loading.Default />
      </RenderIf>
      <GiftReceivePageView {...props} />
    </FormProvider>
  )
}
export default GiftReceivePage
